'use client'

import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'
import { X, type LucideIcon } from 'lucide-react'
import { Dialog, DialogOverlay, DialogTitle, LargeDialogContent } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { cn } from '@/utils/styles/cn'

/** A single sidebar entry for {@link SettingsModalShell}. */
export interface ISettingsShellTab {
    id: string
    label: string
    icon: LucideIcon
}

interface ISettingsModalShellProps {
    open: boolean
    onClose: () => void
    tabs: readonly ISettingsShellTab[]
    activeTab: string
    onTabChange: (id: string) => void
    /** The active tab's content (rendered in the scrollable panel). */
    children: ReactNode
    /** Optional row between the tabs and the panel (e.g. the billing payment notice). */
    subHeader?: ReactNode
    /** Optional sticky footer below the panel (e.g. a "Done" button). */
    footer?: ReactNode
    /**
     * Radix `modal`. The platform settings modal passes `false` so Clerk's portaled dropdowns stay
     * clickable; it then renders its own backdrop (handled here) and ignores Radix close requests,
     * closing only via the header X / backdrop / Escape. Standard modals leave it `true`.
     */
    modal?: boolean
    onEscapeKeyDown?: (event: KeyboardEvent) => void
}

/**
 * The shared chrome for the app's settings modals: the large dialog, the "Settings" header, the
 * desktop sidebar / mobile tab strip (built from `tabs`), and the scrollable content panel.
 *
 * Presentational and tab-agnostic — the platform Settings modal and the content-pages page Settings
 * modal both render through this so they share size, layout, and styling instead of duplicating it.
 */
export function SettingsModalShell({
    open,
    onClose,
    tabs,
    activeTab,
    onTabChange,
    children,
    subHeader,
    footer,
    modal = true,
    onEscapeKeyDown,
}: ISettingsModalShellProps) {
    // With `modal={false}` Radix renders no overlay (so Clerk dropdowns stay clickable), so we
    // portal our own backdrop. In standard modal mode Radix's overlay handles the outside click.
    const backdrop =
        !modal &&
        typeof document !== 'undefined' &&
        open &&
        createPortal(<DialogOverlay className="z-50" onClick={onClose} />, document.body)

    return (
        <>
            {backdrop}
            <Dialog
                open={open}
                modal={modal}
                // Standard mode: Escape / outside-click close via Radix. Non-modal mode: ignore Radix's
                // close requests (matches the platform deep-link behavior) — close via X / backdrop / Escape.
                onOpenChange={modal ? (next) => !next && onClose() : () => {}}
            >
                <LargeDialogContent
                    onEscapeKeyDown={onEscapeKeyDown}
                    withoutCloseButton
                    className="bg-background flex flex-col gap-0 p-0 md:min-w-[min(90vw,1924px)]"
                >
                    <DialogTitle className="sr-only">Settings</DialogTitle>

                    {/* Header */}
                    <div className="bg-background sticky top-0 z-20 flex min-h-10 w-full items-center justify-between gap-2 rounded-t-[8px] border-b p-4">
                        <h2 className="mb-0 text-[16px] font-semibold">Settings</h2>
                        <Button onClick={onClose} variant="ghost" size="icon" className="h-8 w-8">
                            <X className="h-4 w-4" />
                        </Button>
                    </div>

                    {/* Mobile tabs — horizontal scrollable */}
                    <ShellMobileTabs tabs={tabs} activeTab={activeTab} onTabChange={onTabChange} />

                    {subHeader}

                    {/* Sidebar + panel */}
                    <div className="flex flex-1 overflow-hidden">
                        <ShellSidebar tabs={tabs} activeTab={activeTab} onTabChange={onTabChange} />
                        {footer ? (
                            <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
                                <div className="flex-1 overflow-y-auto p-6">{children}</div>
                                {footer}
                            </div>
                        ) : (
                            <div className="flex-1 overflow-y-auto p-6">{children}</div>
                        )}
                    </div>
                </LargeDialogContent>
            </Dialog>
        </>
    )
}

/** Desktop vertical sidebar (≥ lg). */
function ShellSidebar({
    tabs,
    activeTab,
    onTabChange,
}: {
    tabs: readonly ISettingsShellTab[]
    activeTab: string
    onTabChange: (id: string) => void
}) {
    return (
        <div className="hidden w-[200px] shrink-0 flex-col border-r bg-[#FAFAFA] p-2 lg:flex">
            {tabs.map((tab) => {
                const Icon = tab.icon
                return (
                    <button
                        key={tab.id}
                        onClick={() => onTabChange(tab.id)}
                        className={cn(
                            'flex items-center gap-3 rounded-lg px-3 py-2.5 text-left text-[14px] font-medium transition-colors',
                            activeTab === tab.id
                                ? 'bg-white text-[#171717] shadow-sm'
                                : 'text-[#6B7280] hover:bg-white/50 hover:text-[#374151]'
                        )}
                    >
                        <Icon className="h-4 w-4" />
                        {tab.label}
                    </button>
                )
            })}
        </div>
    )
}

/** Mobile horizontal tab strip (< lg). */
function ShellMobileTabs({
    tabs,
    activeTab,
    onTabChange,
}: {
    tabs: readonly ISettingsShellTab[]
    activeTab: string
    onTabChange: (id: string) => void
}) {
    return (
        <div className="no-scrollbar overflow-x-auto border-b lg:hidden">
            <div className="flex min-w-max">
                {tabs.map((tab) => {
                    const Icon = tab.icon
                    return (
                        <button
                            key={tab.id}
                            onClick={() => onTabChange(tab.id)}
                            className={cn(
                                'flex items-center gap-2 border-b-2 px-4 py-3 text-[14px] font-medium whitespace-nowrap transition-colors',
                                activeTab === tab.id
                                    ? 'border-[#E57100] text-[#E57100]'
                                    : 'border-transparent text-[#6B7280] hover:text-[#374151]'
                            )}
                        >
                            <Icon className="h-4 w-4" />
                            {tab.label}
                        </button>
                    )
                })}
            </div>
        </div>
    )
}
